/**
 * EventDetailPage - Enhanced Mobile-First Event View
 * 
 * Beautiful event detail page with hero image, rich metadata,
 * RSVP/check-in functionality, and seamless mobile UX.
 * 
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7E
 */

import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Calendar, MapPin, Users, Clock, CheckCircle, ChevronRight, Share, Navigation, Edit, Lock } from 'lucide-react';
import { useEvent } from '../hooks/useEvents';
import { useClub } from '../hooks/useClubs';
import { useEventRSVPs } from '../hooks/useRSVPs';
import { useAuth } from '../hooks/useAuth';
import { useAuthModal } from '../contexts/AuthModalContext';
import { TopBar, Button, EmptyState, Avatar, RSVPButtons } from '../components';
import { MembersOnlyLoginPrompt } from '../components/MembersOnlyLoginPrompt';
import { NotMemberGate } from '../components/NotMemberGate';
import CheckinButton from '../components/CheckinButton';
import { createEventDetailMobileMenu } from '../components/MobileContextMenu';
import { ApiError } from '../lib/api';

export default function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { openLoginModal } = useAuthModal();
  
  // Get current time once for consistent calculations
  const [currentTime] = useState(() => Date.now());
  
  // Fetch event using React Query with staleTime for public event caching
  const { data: eventData, isLoading: loading, error } = useEvent(id || '', {
    staleTime: 5 * 60 * 1000, // 5 minutes (public events cached on backend)
  });
  const event = eventData;
  
  // Get current user for permission checks (needed for auth-guarded queries)
  const { user, logout } = useAuth();
  
  // Fetch club data
  const { data: clubData } = useClub(event?.club_slug || '');
  // Use club data if available, fallback to event.club_id
  
  // Fetch RSVP data (Issue #604 Phase 2: only when user logged in)
  const { data: rsvpData } = useEventRSVPs(event?.id || '', user?.id);

  const isWithinCheckinWindow = (event: { starts_at: number; checkin_window_before_min: number | null; checkin_window_after_min: number | null }) => {
    const windowBefore = (event.checkin_window_before_min || 30) * 60 * 1000;
    const windowAfter = (event.checkin_window_after_min || 30) * 60 * 1000;
    const startsAtMs = event.starts_at * 1000;
    const windowStart = startsAtMs - windowBefore;
    const windowEnd = startsAtMs + windowAfter;
    return currentTime >= windowStart && currentTime <= windowEnd;
  };

  const formatEventDate = (timestamp: number) => {
    // Handle invalid timestamps including undefined
    if (timestamp === undefined || timestamp === null || !timestamp || isNaN(timestamp) || timestamp <= 0) {
      return {
        day: '--',
        month: '---',
        weekday: '---',
        year: new Date().getFullYear()
      };
    }
    
    // Try parsing as seconds first (our standard)
    let date = new Date(timestamp * 1000);
    
    // If that gives us an invalid date or unreasonable date, try as milliseconds
    if (isNaN(date.getTime()) || date.getFullYear() < 2020 || date.getFullYear() > 2030) {
      // Try parsing as milliseconds instead
      date = new Date(timestamp);
    }
    
    // Final validation - check if the date is still invalid
    if (isNaN(date.getTime())) {
      return {
        day: '--',
        month: '---',
        weekday: '---',
        year: new Date().getFullYear()
      };
    }
    
    return {
      day: date.toLocaleDateString('en-US', { day: 'numeric' }),
      month: date.toLocaleDateString('en-US', { month: 'short' }),
      weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
      year: date.getFullYear()
    };
  };

  const formatEventTime = (timestamp: number) => {
    // Handle invalid timestamps including undefined
    if (timestamp === undefined || timestamp === null || !timestamp || isNaN(timestamp) || timestamp <= 0) {
      return 'Time TBD';
    }
    
    // Try parsing as seconds first (our standard)
    let date = new Date(timestamp * 1000);
    
    // If that gives us an invalid date or unreasonable date, try as milliseconds
    if (isNaN(date.getTime()) || date.getFullYear() < 2020 || date.getFullYear() > 2030) {
      // Try parsing as milliseconds instead
      date = new Date(timestamp);
    }
    
    // Final validation
    if (isNaN(date.getTime())) {
      return 'Time TBD';
    }
    
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getEventStatus = () => {
    const now = currentTime / 1000;
    const starts = event?.starts_at || 0;
    const ends = event?.ends_at || starts + 7200; // Default 2hr duration
    
    if (now < starts) return 'upcoming';
    if (now >= starts && now <= ends) return 'live';
    return 'ended';
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Event" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-12 text-center">
            <p className="text-text-muted">Loading event...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error handling - explicit status + code checking (defense in depth)
  if (error) {
    const apiError = error as ApiError;
    
    // 401 Unauthorized - Members-only event, not logged in
    if (apiError?.status === 401 && apiError?.code === 'UNAUTHORIZED') {
      return (
        <MembersOnlyLoginPrompt
          onLoginClick={() => openLoginModal(location.pathname)}
        />
      );
    }
    
    // 403 Forbidden - Authenticated but not a club member
    if (apiError?.status === 403 && apiError?.code === 'FORBIDDEN') {
      return <NotMemberGate clubSlug={event?.club_slug || undefined} />;
    }
    
    // 404 Not Found
    if (apiError?.status === 404 && apiError?.code === 'NOT_FOUND') {
      return (
        <div className="flex flex-col bg-oro-bg">
          <TopBar title="Event" onBack={() => navigate(-1)} />
          <div className="flex-1 overflow-y-auto">
            <div className="px-4 py-12">
              <EmptyState
                variant="default"
                icon={<Calendar className="w-12 h-12" />}
                title="Event not found"
                message="This event may have been deleted or you don't have access to view it"
                actionLabel="Go Back"
                onAction={() => navigate(-1)}
              />
            </div>
          </div>
        </div>
      );
    }
    
    // 500 Server Error
    const errorMessage = apiError?.message || 'Unable to load event';
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Event" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-12">
            <EmptyState
              variant="default"
              icon={<Calendar className="w-12 h-12" />}
              title="Something went wrong"
              message={errorMessage}
              actionLabel="Try Again"
              onAction={() => window.location.reload()}
            />
          </div>
        </div>
      </div>
    );
  }

  // No event data after successful request
  if (!event || !id) {
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Event" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-12">
            <EmptyState
              variant="default"
              icon={<Calendar className="w-12 h-12" />}
              title="Event not found"
              message="This event may have been deleted"
              actionLabel="Go Back"
              onAction={() => navigate(-1)}
            />
          </div>
        </div>
      </div>
    );
  }

  const canCheckIn = isWithinCheckinWindow(event);
  const attendeeCount = event.attendee_count || 0;
  const eventStatus = getEventStatus();
  const dateInfo = formatEventDate(event.starts_at);

  // Commented out function stubs for future enhancements
  // const handleRSVP = () => {
  //   console.log('RSVP to event:', event.id);
  // };

  // Determine if user can edit this event
  const canEditEvent = () => {
    if (!user || !event) return false;
    
    // Platform admin can edit any event
    if (user.is_platform_admin) return true;
    
    // Event creator can edit their event
    if (event.created_by === user.id) return true;
    
    // Club leaders/owners can edit club events
    const clubRole = clubData?.membership?.role;
    const isOwner = clubData?.membership?.is_owner;
    if ((clubRole === 'leader' || isOwner) && event.club_id === clubData?.club?.id) return true;
    
    return false;
  };
  
  const handleEdit = () => {
    navigate(`/event/${event.id}/edit`);
  };

  const handleShare = () => {
    if (navigator.share && event) {
      navigator.share({
        title: event.title,
        text: `Check out this event: ${event.title}`,
        url: window.location.href
      }).catch(() => {
        // Share failed or was cancelled - silent fail
      });
    } else if (event) {
      // Fallback: Copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      // Could show a toast notification here
    }
  };

  // Create mobile menu configuration
  const eventDetailMenu = event ? createEventDetailMobileMenu(
    navigate,
    logout,
    event.id,
    canEditEvent(),
    handleShare
  ) : undefined;

  return (
    <div className="flex flex-col bg-oro-bg">
      <TopBar 
        title=""
        onBack={() => navigate(-1)}
        mobileMenu={eventDetailMenu}
      />

      <div className="flex-1 overflow-y-auto">
        {/* Hero Section with Date Badge */}
        <div className="relative">
          {/* Hero Image */}
          <div className="relative h-64 overflow-hidden">
            {event.hero_image_url || clubData?.club?.hero_image_url ? (
              <img
                src={event.hero_image_url || clubData?.club?.hero_image_url || ''}
                alt={event.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-linear-to-br from-accent/20 to-accent/5 flex items-center justify-center">
                <Calendar className="w-20 h-20 text-accent/60" />
              </div>
            )}
            <div className="absolute inset-0 bg-linear-to-t from-black/70 via-transparent to-transparent" />
          </div>

          {/* Floating Date Badge */}
          <div className="absolute top-4 right-4">
            <div className="bg-oro-surface/90 backdrop-blur-sm border border-oro-border rounded-xl p-3 shadow-card">
              <div className="text-center">
                <div className="text-text-muted text-xs font-medium uppercase tracking-wide">
                  {dateInfo.weekday}
                </div>
                <div className="text-text-primary text-xl font-bold leading-none mt-1">
                  {dateInfo.day}
                </div>
                <div className="text-text-secondary text-xs font-medium">
                  {dateInfo.month}
                </div>
              </div>
            </div>
          </div>

          {/* Event Status Badge */}
          <div className="absolute top-4 left-4">
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              eventStatus === 'live' 
                ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                : eventStatus === 'upcoming'
                ? 'bg-accent/20 text-accent border border-accent/40'
                : 'bg-oro-surface-alt text-text-muted border border-oro-border'
            }`}>
              {eventStatus === 'live' ? '🔴 Live' : eventStatus === 'upcoming' ? 'Upcoming' : 'Ended'}
            </div>
          </div>

          {/* Title Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h1 className="text-white font-bold text-2xl leading-tight mb-2">
              {event.title}
            </h1>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-white/80" />
                <span className="text-white/90 text-sm">
                  {formatEventTime(event.starts_at)}
                </span>
              </div>
              {/* Visibility badge (Issue #427) */}
              {event.visibility === 'members_only' && (
                <div className="inline-flex items-center gap-1.5 bg-white/20 backdrop-blur-sm border border-white/30 rounded-lg px-2 py-1">
                  <Lock className="w-3 h-3 text-white" />
                  <span className="text-white text-xs font-medium">Members-only</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="px-4 py-6 space-y-6">
          {/* Quick Info Cards */}
          <div className={`grid gap-3 ${event.distance_km && event.distance_km > 0 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2'}`}>
            {/* Location Card */}
            {event.location_name && (
              <div className="bg-oro-surface border border-oro-border rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin className="w-4 h-4 text-accent" />
                  <span className="text-text-muted text-xs font-medium">Location</span>
                </div>
                <p className="text-text-primary text-sm font-medium truncate">
                  {event.location_name}
                </p>
                {event.location_lat && event.location_lng && (
                  <button
                    onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${event.location_lat},${event.location_lng}`, '_blank')}
                    className="flex items-center gap-1 text-accent text-xs mt-1 hover:text-accent/80 transition-colors"
                  >
                    <Navigation className="w-3 h-3" />
                    Directions
                  </button>
                )}
              </div>
            )}

            {/* Distance Card */}
            {event.distance_km && event.distance_km > 0 && (
              <div className="bg-oro-surface border border-oro-border rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <svg className="w-4 h-4 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-text-muted text-xs font-medium">Distance</span>
                </div>
                <p className="text-text-primary text-sm font-medium">
                  {event.distance_km} km
                </p>
                <p className="text-text-muted text-xs mt-1">
                  {(event.distance_km * 0.621371).toFixed(1)} miles
                </p>
              </div>
            )}

            {/* Attendance Card */}
            <div className="bg-oro-surface border border-oro-border rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <Users className="w-4 h-4 text-accent" />
                <span className="text-text-muted text-xs font-medium">RSVPs</span>
              </div>
              {rsvpData?.counts ? (
                <div>
                  <p className="text-text-primary text-sm font-medium">
                    {rsvpData.counts.yes} going
                    {rsvpData.counts.maybe > 0 && `, ${rsvpData.counts.maybe} maybe`}
                  </p>
                  <div className="flex gap-1 mt-2">
                    <div className="flex-1 h-1.5 bg-green-500/20 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full transition-all duration-300"
                        style={{ width: rsvpData.counts.total > 0 ? `${(rsvpData.counts.yes / rsvpData.counts.total) * 100}%` : '0%' }}
                      />
                    </div>
                    {rsvpData.counts.maybe > 0 && (
                      <div className="flex-1 h-1.5 bg-yellow-500/20 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-yellow-500 rounded-full transition-all duration-300"
                          style={{ width: `${(rsvpData.counts.maybe / rsvpData.counts.total) * 100}%` }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <p className="text-text-primary text-sm font-medium">
                  {attendeeCount} {attendeeCount === 1 ? 'person' : 'people'}
                </p>
              )}
            </div>
          </div>

          {/* Description */}
          {event.description && (
            <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
              <h3 className="text-text-primary font-semibold mb-3">About This Event</h3>
              <p className="text-text-secondary leading-relaxed">
                {event.description}
              </p>
            </div>
          )}

          {/* Organized By */}
          {clubData && (
            <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
              <h3 className="text-text-primary font-semibold mb-3">Organized By</h3>
              <button
                onClick={() => navigate(`/clubs/${event.club_slug}`)}
                className="w-full flex items-center gap-3 text-left group hover:bg-oro-surface-alt rounded-lg p-2 -m-2 transition-colors"
              >
                <Avatar
                  name={clubData.club.name}
                  profile_image_url={clubData.club.hero_image_url}
                  size="md"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-text-primary font-medium group-hover:text-accent transition-colors">
                    {clubData.club.name}
                  </p>
                  <p className="text-text-muted text-sm">
                    View club profile
                  </p>
                </div>
                <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-accent transition-colors" />
              </button>
            </div>
          )}

          {/* Event Details */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
            <h3 className="text-text-primary font-semibold mb-3">Event Details</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-accent" />
                <div>
                  <p className="text-text-primary text-sm">
                    {dateInfo.weekday}, {dateInfo.month} {dateInfo.day}, {dateInfo.year}
                  </p>
                  <p className="text-text-muted text-xs">
                    {formatEventTime(event.starts_at)}
                    {event.ends_at && ` - ${formatEventTime(event.ends_at)}`}
                  </p>
                </div>
              </div>
              
              {event.check_ins_enabled && (
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-4 h-4 text-accent" />
                  <div>
                    <p className="text-text-primary text-sm">Check-in Available</p>
                    <p className="text-text-muted text-xs">
                      {canCheckIn ? 'Check-in window is open' : 'Check-in opens 30 minutes before event'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pb-8">
            {canCheckIn ? (
              <div>
                <CheckinButton 
                  event={event}
                />
                {event.checkin_radius_m && (
                  <p className="text-text-muted text-xs text-center mt-2">
                    Check-in radius: {event.checkin_radius_m}m from event location
                  </p>
                )}
              </div>
            ) : eventStatus === 'upcoming' ? (
              <RSVPButtons eventId={event.id} />
            ) : (
              <div className="bg-oro-surface-alt border border-oro-border rounded-xl p-4 text-center">
                <p className="text-text-muted text-sm">
                  {eventStatus === 'live' ? 'Event is currently in progress' : 'This event has ended'}
                </p>
              </div>
            )}

            {/* Edit Button (if user has permissions) */}
            {canEditEvent() && (
              <Button
                variant="primary"
                size="sm"
                onClick={handleEdit}
                className="flex items-center justify-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit Event
              </Button>
            )}
            
            {/* Secondary Actions */}
            <div className={`grid gap-3 ${
              event.location_lat && event.location_lng ? 'grid-cols-2' : 'grid-cols-1'
            }`}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: event.title,
                      text: `Check out this event: ${event.title}`,
                      url: window.location.href
                    });
                  }
                }}
                className="flex items-center justify-center gap-2"
              >
                <Share className="w-4 h-4" />
                Share
              </Button>
              
              {event.location_lat && event.location_lng && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${event.location_lat},${event.location_lng}`, '_blank')}
                  className="flex items-center justify-center gap-2"
                >
                  <Navigation className="w-4 h-4" />
                  Directions
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
